<template>
  <div class="conclusions-page" v-loading="loading">
    <section class="summary-grid">
      <el-card v-for="card in summaryCards" :key="card.key" shadow="never" class="summary-card">
        <div class="summary-icon" :class="card.tone"><i :class="card.icon"></i></div>
        <div class="summary-content">
          <span>{{ card.label }}</span>
          <strong>{{ card.value }}</strong>
          <p>{{ card.desc }}</p>
        </div>
      </el-card>
    </section>

    <el-card shadow="never" class="archive-card">
      <div slot="header" class="archive-header">
        <div>
          <strong>分析结论档案</strong>
          <p>按标的查找分析结论，并通过留痕窗口判断数据、证据和发布回读是否完整。</p>
        </div>
        <el-button
          size="small"
          icon="el-icon-refresh"
          :loading="loading"
          @click="refreshData"
        >刷新</el-button>
      </div>

      <div class="filter-row">
        <StockCodeInput
          v-model="searchForm.ts_code"
          placeholder="输入股票代码或名称"
          width="100%"
        />
        <el-select v-model="searchForm.version_type" placeholder="全部版本" clearable size="small">
          <el-option label="实时分析" value="live" />
          <el-option label="正式分析" value="formal" />
          <el-option label="盘中临时版" value="盘中临时版" />
          <el-option label="盘后版" value="盘后版" />
          <el-option label="单票分析版" value="单票分析版" />
        </el-select>
        <el-button size="small" type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
        <el-button size="small" @click="handleReset">重置</el-button>
      </div>

      <div class="result-line">
        <span>{{ hasActiveFilters ? '筛选结果' : '当前读取窗口' }}</span>
        <strong>{{ conclusions.length }}</strong>
        <span>条</span>
      </div>

      <el-alert
        v-if="loadError"
        title="分析结论暂时无法读取，请稍后刷新。"
        type="warning"
        :closable="false"
        show-icon
        class="inline-alert"
      />

      <el-table
        :data="conclusions"
        size="small"
        stripe
        empty-text="暂无符合条件的分析结论"
        class="conclusions-table desktop-list"
      >
        <el-table-column label="分析对象" min-width="230">
          <template slot-scope="{ row }">
            <div class="stock-cell">
              <strong>{{ row.name || '未命名标的' }}</strong>
              <span>{{ row.ts_code || '代码未记录' }}</span>
              <el-tooltip :content="row.conclusion_id || '结论编号未记录'" placement="top">
                <code>{{ shortConclusionId(row.conclusion_id) }}</code>
              </el-tooltip>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="结论摘要" min-width="390">
          <template slot-scope="{ row }">
            <div class="summary-text" :title="summaryText(row.summary)">
              {{ summaryText(row.summary) }}
            </div>
          </template>
        </el-table-column>

        <el-table-column label="版本 / 建议" width="125">
          <template slot-scope="{ row }">
            <el-tag size="mini" effect="plain" :type="versionTagType(row.version_type)">
              {{ versionText(row.version_type) }}
            </el-tag>
            <div v-if="hasAction(row.action)" class="decision-text">
              建议：{{ actionText(row.action) }}
            </div>
            <div v-else class="sub-text">未记录建议</div>
          </template>
        </el-table-column>

        <el-table-column label="数据状态" width="125">
          <template slot-scope="{ row }">
            <el-tag size="mini" effect="plain" :type="dataStatusTagType(row.data_status)">
              {{ dataStatusText(row.data_status) }}
            </el-tag>
            <div class="sub-text">
              {{ row.data_status === 'official' ? '正式数据口径' : '请结合留痕判断' }}
            </div>
          </template>
        </el-table-column>

        <el-table-column label="创建时间" width="170">
          <template slot-scope="{ row }">
            <el-tooltip :content="String(row.created_at || '未记录')" placement="top">
              <span class="time-text">{{ formatBeijing(row.created_at) }}</span>
            </el-tooltip>
          </template>
        </el-table-column>

        <el-table-column label="操作" width="185" fixed="right">
          <template slot-scope="{ row }">
            <div class="action-buttons">
              <el-button
                size="mini"
                type="primary"
                plain
                icon="el-icon-document-checked"
                @click="showReceipt(row)"
              >查看留痕</el-button>
              <el-button type="text" size="mini" @click="showDetail(row)">详情</el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <div class="mobile-list">
        <article v-for="row in conclusions" :key="row.conclusion_id" class="conclusion-card">
          <div class="mobile-card-head">
            <div>
              <strong>{{ row.name || '未命名标的' }}</strong>
              <span>{{ row.ts_code || '代码未记录' }}</span>
            </div>
            <el-tag size="mini" effect="plain" :type="dataStatusTagType(row.data_status)">
              {{ dataStatusText(row.data_status) }}
            </el-tag>
          </div>
          <p class="mobile-summary">{{ summaryText(row.summary) }}</p>
          <div class="mobile-meta">
            <span>{{ versionText(row.version_type) }}</span>
            <span>{{ formatBeijing(row.created_at) }}</span>
          </div>
          <div class="mobile-id">{{ shortConclusionId(row.conclusion_id) }}</div>
          <div class="mobile-actions">
            <el-button size="small" @click="showDetail(row)">详情</el-button>
            <el-button size="small" type="primary" plain @click="showReceipt(row)">查看留痕</el-button>
          </div>
        </article>
        <div v-if="!conclusions.length && !loading" class="empty-state">暂无符合条件的分析结论</div>
      </div>

      <el-pagination
        v-if="total > pagination.size"
        :current-page="pagination.page"
        :page-size="pagination.size"
        :total="total"
        layout="total, prev, pager, next"
        class="pagination"
        @current-change="handlePageChange"
      />
    </el-card>

    <el-dialog
      title="结论详情"
      :visible.sync="detailVisible"
      width="760px"
      custom-class="conclusion-detail-dialog"
      :close-on-click-modal="false"
    >
      <div v-loading="detailLoading">
        <template v-if="currentConclusion">
          <section class="detail-hero">
            <div class="detail-heading">
              <div>
                <span>分析对象</span>
                <strong>{{ currentConclusion.name || '未命名标的' }}</strong>
                <p>{{ currentConclusion.ts_code || '代码未记录' }}</p>
              </div>
              <div class="detail-tags">
                <el-tag size="small" effect="plain" :type="versionTagType(currentConclusion.version_type)">
                  {{ versionText(currentConclusion.version_type) }}
                </el-tag>
                <el-tag size="small" effect="plain" :type="dataStatusTagType(currentConclusion.data_status)">
                  {{ dataStatusText(currentConclusion.data_status) }}
                </el-tag>
              </div>
            </div>
            <p class="detail-summary">{{ summaryText(currentConclusion.summary) }}</p>
          </section>

          <div class="detail-meta-grid">
            <div>
              <span>结论编号</span>
              <code>{{ currentConclusion.conclusion_id || '未记录' }}</code>
            </div>
            <div>
              <span>创建时间（北京时间）</span>
              <strong>{{ formatBeijing(currentConclusion.created_at) }}</strong>
            </div>
          </div>

          <section
            v-if="hasEvidence(currentConclusion.main_evidence) || hasEvidence(currentConclusion.main_counter_evidence)"
            class="detail-section"
          >
            <div class="section-title">主要依据</div>
            <div class="evidence-grid">
              <div class="evidence-panel positive">
                <strong>支持证据</strong>
                <div v-if="hasEvidence(currentConclusion.main_evidence)">
                  <p v-for="(item, index) in currentConclusion.main_evidence" :key="'e-' + index">
                    <span>{{ index + 1 }}</span>{{ evidenceText(item) }}
                  </p>
                </div>
                <span v-else class="sub-text">未记录</span>
              </div>
              <div class="evidence-panel caution">
                <strong>反向证据</strong>
                <div v-if="hasEvidence(currentConclusion.main_counter_evidence)">
                  <p v-for="(item, index) in currentConclusion.main_counter_evidence" :key="'c-' + index">
                    <span>{{ index + 1 }}</span>{{ evidenceText(item) }}
                  </p>
                </div>
                <span v-else class="sub-text">未记录</span>
              </div>
            </div>
          </section>

          <section
            v-if="objectEntries(currentConclusion.key_levels).length || objectEntries(currentConclusion.probability).length"
            class="detail-section"
          >
            <div class="section-title">关键判断</div>
            <div class="detail-two-column">
              <div v-if="objectEntries(currentConclusion.key_levels).length" class="business-panel">
                <strong>关键位置</strong>
                <div class="business-grid">
                  <div v-for="entry in objectEntries(currentConclusion.key_levels)" :key="'level-' + entry.key">
                    <span>{{ entry.label }}</span>
                    <strong>{{ entry.value }}</strong>
                  </div>
                </div>
              </div>
              <div v-if="objectEntries(currentConclusion.probability).length" class="business-panel">
                <strong>概率评估</strong>
                <div class="business-grid">
                  <div v-for="entry in objectEntries(currentConclusion.probability)" :key="'prob-' + entry.key">
                    <span>{{ entry.label }}</span>
                    <strong>{{ entry.value }}</strong>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section v-if="objectEntries(currentConclusion.verification).length" class="detail-section">
            <div class="section-title">验证结果</div>
            <div class="business-grid verification-grid">
              <div v-for="entry in objectEntries(currentConclusion.verification)" :key="'verify-' + entry.key">
                <span>{{ entry.label }}</span>
                <strong>{{ entry.value }}</strong>
              </div>
            </div>
          </section>
        </template>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button @click="detailVisible = false">关闭</el-button>
        <el-button
          type="primary"
          plain
          icon="el-icon-document-checked"
          :disabled="!currentConclusion || !currentConclusion.conclusion_id"
          @click="openReceiptFromDetail"
        >查看正式留痕</el-button>
      </div>
    </el-dialog>

    <FormalReceiptDialog
      :visible.sync="receiptVisible"
      :conclusion-id="receiptConclusionId"
    />
  </div>
</template>

<script>
import StockCodeInput from '@/components/StockCodeInput.vue'
import FormalReceiptDialog from '@/components/FormalReceiptDialog.vue'
import { conclusionApi } from '@/api'

export default {
  name: 'Conclusions',
  components: { StockCodeInput, FormalReceiptDialog },
  data() {
    return {
      loading: false,
      loadError: false,
      conclusions: [],
      total: 0,
      searchForm: {
        ts_code: '',
        version_type: ''
      },
      pagination: {
        page: 1,
        size: 20
      },
      detailVisible: false,
      detailLoading: false,
      currentConclusion: null,
      receiptVisible: false,
      receiptConclusionId: ''
    }
  },
  computed: {
    hasActiveFilters() {
      return Boolean(String(this.searchForm.ts_code || '').trim() || this.searchForm.version_type)
    },
    latestConclusion() {
      return this.conclusions.slice().sort((a, b) => this.timeValue(b.created_at) - this.timeValue(a.created_at))[0] || null
    },
    summaryCards() {
      const official = this.conclusions.filter(row => row.data_status === 'official').length
      const pending = Math.max(0, this.conclusions.length - official)
      const latest = this.latestConclusion
      return [
        {
          key: 'results',
          label: '当前结果',
          value: String(this.conclusions.length),
          desc: this.hasActiveFilters ? '筛选后的读取结果' : '当前读取窗口',
          icon: 'el-icon-document',
          tone: 'blue'
        },
        {
          key: 'official',
          label: '正式数据',
          value: String(official),
          desc: '使用正式数据口径',
          icon: 'el-icon-circle-check',
          tone: official ? 'green' : 'gray'
        },
        {
          key: 'pending',
          label: '待确认',
          value: String(pending),
          desc: pending ? '建议打开留痕判断' : '当前结果均为正式',
          icon: 'el-icon-warning-outline',
          tone: pending ? 'amber' : 'green'
        },
        {
          key: 'latest',
          label: '最近生成',
          value: latest ? this.formatDateShort(latest.created_at) : '-',
          desc: latest ? this.formatTimeShort(latest.created_at) : '暂无生成记录',
          icon: 'el-icon-time',
          tone: 'purple'
        }
      ]
    }
  },
  mounted() {
    this.loadConclusions()
  },
  methods: {
    async refreshData() {
      return this.loadConclusions()
    },
    async loadConclusions() {
      this.loading = true
      this.loadError = false
      try {
        const params = {
          limit: this.pagination.size,
          offset: (this.pagination.page - 1) * this.pagination.size
        }
        const tsCode = String(this.searchForm.ts_code || '').trim()
        if (tsCode) params.ts_code = tsCode
        if (this.searchForm.version_type) params.version_type = this.searchForm.version_type

        const res = await conclusionApi.getList(params)
        this.conclusions = Array.isArray(res && res.conclusions) ? res.conclusions : []
        const total = Number(res && res.total)
        this.total = Number.isFinite(total) ? total : this.conclusions.length
      } catch (error) {
        this.conclusions = []
        this.total = 0
        this.loadError = true
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.pagination.page = 1
      this.loadConclusions()
    },
    handleReset() {
      this.searchForm = { ts_code: '', version_type: '' }
      this.pagination.page = 1
      this.loadConclusions()
    },
    handlePageChange(page) {
      this.pagination.page = page
      this.loadConclusions()
    },
    async showDetail(row) {
      this.currentConclusion = Object.assign({}, row)
      this.detailVisible = true
      this.detailLoading = true
      try {
        const response = await conclusionApi.getDetail(row.conclusion_id)
        if (response && response.conclusion) {
          this.currentConclusion = Object.assign({}, row, response.conclusion)
        }
      } catch (error) {
        this.$message.warning('完整详情暂时不可用，已展示当前结论摘要。')
      } finally {
        this.detailLoading = false
      }
    },
    showReceipt(row) {
      this.receiptConclusionId = typeof row === 'string' ? row : (row && row.conclusion_id) || ''
      this.receiptVisible = true
    },
    openReceiptFromDetail() {
      const conclusionId = this.currentConclusion && this.currentConclusion.conclusion_id
      if (!conclusionId) return
      this.detailVisible = false
      this.$nextTick(() => this.showReceipt(conclusionId))
    },
    summaryText(value) {
      const text = String(value || '').trim()
      return text || '该结论暂未记录摘要。'
    },
    shortConclusionId(value) {
      const text = String(value || '').trim()
      if (!text) return '编号未记录'
      if (text.length <= 24) return text
      return text.slice(0, 15) + '…' + text.slice(-4)
    },
    versionText(value) {
      const map = {
        live: '实时分析',
        formal: '正式分析',
        review: '复核分析',
        '盘中临时版': '盘中临时',
        '盘后版': '盘后分析',
        '单票分析版': '单票分析'
      }
      return map[value] || String(value || '版本未记录')
    },
    versionTagType(value) {
      if (value === 'formal' || value === '盘后版') return 'success'
      if (value === 'live' || value === '单票分析版') return 'primary'
      if (value === '盘中临时版') return 'warning'
      return 'info'
    },
    hasAction(value) {
      return Boolean(String(value || '').trim())
    },
    actionText(value) {
      const map = {
        buy: '买入',
        hold: '持有',
        watch: '观察',
        wait: '等待',
        defend: '防守',
        '买': '买入',
        '等': '等待'
      }
      return map[value] || String(value || '未记录')
    },
    dataStatusText(value) {
      const map = {
        official: '正式',
        provisional: '待确认',
        partial_success: '部分可用',
        degraded: '降级可用',
        failed: '不可用'
      }
      return map[value] || '待确认'
    },
    dataStatusTagType(value) {
      if (value === 'official') return 'success'
      if (value === 'failed') return 'danger'
      return 'warning'
    },
    hasEvidence(value) {
      return Array.isArray(value) && value.length > 0
    },
    evidenceText(value) {
      if (value === null || value === undefined || value === '') return '未记录'
      if (typeof value !== 'object') return String(value)
      return String(value.summary || value.title || value.content || value.evidence || value.name || '已记录一项证据')
    },
    objectEntries(value) {
      if (!value || typeof value !== 'object' || Array.isArray(value)) return []
      return Object.keys(value).map(key => ({
        key,
        label: this.fieldLabel(key),
        value: this.displayValue(value[key])
      }))
    },
    fieldLabel(key) {
      const labels = {
        support: '支撑位',
        resistance: '压力位',
        target: '目标位',
        stop_loss: '止损位',
        invalidation: '失效条件',
        bullish: '偏强概率',
        neutral: '中性概率',
        bearish: '偏弱概率',
        up: '上涨概率',
        flat: '震荡概率',
        down: '下跌概率',
        verified: '是否验证',
        result: '验证结论',
        return_1d: '1日表现',
        return_3d: '3日表现',
        return_5d: '5日表现',
        return_10d: '10日表现',
        max_drawdown: '最大回撤'
      }
      return labels[key] || String(key).replace(/_/g, ' ')
    },
    displayValue(value) {
      if (value === null || value === undefined || value === '') return '未记录'
      if (value === true) return '是'
      if (value === false) return '否'
      if (Array.isArray(value)) return value.map(item => this.displayValue(item)).join('、')
      if (typeof value === 'object') {
        return Object.keys(value).map(key => this.fieldLabel(key) + '：' + this.displayValue(value[key])).join('；')
      }
      return String(value)
    },
    normalizeDate(value) {
      if (!value) return null
      if (value instanceof Date) return value
      let text = String(value).trim().replace(' ', 'T')
      if (!/(Z|[+-]\d{2}:?\d{2})$/.test(text)) text += '+08:00'
      const date = new Date(text)
      return Number.isNaN(date.getTime()) ? null : date
    },
    timeValue(value) {
      const date = this.normalizeDate(value)
      return date ? date.getTime() : 0
    },
    dateParts(value) {
      const date = this.normalizeDate(value)
      if (!date) return null
      return new Intl.DateTimeFormat('zh-CN', {
        timeZone: 'Asia/Shanghai',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }).formatToParts(date).reduce((result, item) => {
        result[item.type] = item.value
        return result
      }, {})
    },
    formatBeijing(value) {
      const parts = this.dateParts(value)
      if (!parts) return '未记录'
      return parts.year + '-' + parts.month + '-' + parts.day + ' ' + parts.hour + ':' + parts.minute
    },
    formatDateShort(value) {
      const parts = this.dateParts(value)
      return parts ? parts.month + '-' + parts.day : '-'
    },
    formatTimeShort(value) {
      const parts = this.dateParts(value)
      return parts ? parts.hour + ':' + parts.minute + ' 北京时间' : '时间未记录'
    }
  }
}
</script>

<style scoped>
.conclusions-page /deep/ .conclusion-detail-dialog { max-width: calc(100vw - 28px); border-radius: 16px; overflow: hidden; }
.conclusions-page /deep/ .conclusion-detail-dialog .el-dialog__header { border-bottom: 1px solid #e8eef6; }
.conclusions-page { max-width: 1460px; margin: 0 auto; }
.summary-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 14px; margin-bottom: 16px; }
.summary-card { border-radius: 14px; border-color: #dfe7f2; }
.summary-card /deep/ .el-card__body { display: flex; align-items: center; gap: 14px; padding: 16px 18px; }
.summary-icon { flex: 0 0 42px; width: 42px; height: 42px; border-radius: 13px; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 20px; background: #94a3b8; }
.summary-icon.blue { background: #2563eb; }
.summary-icon.green { background: #16a34a; }
.summary-icon.amber { background: #f59e0b; }
.summary-icon.purple { background: #7c3aed; }
.summary-icon.gray { background: #94a3b8; }
.summary-content { min-width: 0; }
.summary-content span { color: #64748b; font-size: 12px; }
.summary-content strong { display: block; margin: 3px 0; color: #0f172a; font-size: 23px; line-height: 1.2; }
.summary-content p { margin: 0; color: #94a3b8; font-size: 12px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.archive-card { border-radius: 14px; border-color: #dfe7f2; overflow: hidden; }
.archive-card /deep/ .el-card__header { padding: 16px 18px; }
.archive-card /deep/ .el-card__body { padding: 16px 18px 18px; }
.archive-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; }
.archive-header strong { color: #0f172a; font-size: 16px; }
.archive-header p { margin: 5px 0 0; color: #64748b; font-size: 12px; line-height: 1.5; }
.filter-row { display: grid; grid-template-columns: minmax(240px, 1fr) 190px 88px 74px; gap: 10px; align-items: center; }
.filter-row /deep/ .el-autocomplete { width: 100% !important; }
.result-line { display: flex; align-items: baseline; gap: 5px; margin: 13px 2px 8px; color: #94a3b8; font-size: 12px; }
.result-line strong { color: #334155; font-size: 14px; }
.inline-alert { margin-bottom: 12px; }

.conclusions-table /deep/ th { background: #f8fafc; color: #64748b; font-weight: 600; }
.conclusions-table /deep/ td { padding: 11px 0; }
.conclusions-table /deep/ .cell { line-height: 1.5; }
.stock-cell { min-width: 0; }
.stock-cell strong { display: block; color: #0f172a; font-size: 14px; }
.stock-cell > span { display: block; margin-top: 3px; color: #64748b; font-size: 12px; }
.stock-cell code { display: block; max-width: 205px; margin-top: 4px; color: #94a3b8; font-family: Consolas, Monaco, monospace; font-size: 11px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.summary-text { color: #334155; line-height: 1.65; display: -webkit-box; overflow: hidden; -webkit-box-orient: vertical; -webkit-line-clamp: 2; }
.decision-text { margin-top: 6px; color: #475569; font-size: 12px; }
.sub-text { margin-top: 5px; color: #94a3b8; font-size: 11px; line-height: 1.4; }
.time-text { color: #475569; font-variant-numeric: tabular-nums; }
.action-buttons { display: flex; align-items: center; gap: 2px; }
.action-buttons .el-button + .el-button { margin-left: 2px; }
.mobile-list { display: none; }
.pagination { margin-top: 16px; text-align: right; }

.detail-hero { padding: 17px; border: 1px solid #dbe7f5; border-radius: 14px; background: #f7faff; }
.detail-heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; }
.detail-heading span { color: #64748b; font-size: 12px; }
.detail-heading strong { display: block; margin-top: 4px; color: #0f172a; font-size: 20px; }
.detail-heading p { margin: 3px 0 0; color: #64748b; }
.detail-tags { display: flex; gap: 7px; }
.detail-summary { margin: 14px 0 0; padding-top: 13px; border-top: 1px solid #e1eaf5; color: #334155; line-height: 1.75; }
.detail-meta-grid { display: grid; grid-template-columns: 1.3fr 1fr; gap: 12px; margin-top: 13px; }
.detail-meta-grid > div { min-width: 0; padding: 12px 14px; border-radius: 11px; background: #f8fafc; }
.detail-meta-grid span { display: block; color: #64748b; font-size: 12px; }
.detail-meta-grid strong, .detail-meta-grid code { display: block; margin-top: 6px; color: #334155; font-size: 12px; word-break: break-all; }
.detail-meta-grid code { color: #1d4ed8; font-family: Consolas, Monaco, monospace; }
.detail-section { margin-top: 20px; }
.section-title { margin-bottom: 10px; color: #0f172a; font-size: 14px; font-weight: 700; }
.evidence-grid, .detail-two-column { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
.evidence-panel, .business-panel { padding: 14px; border: 1px solid #e2e8f0; border-radius: 12px; background: #fff; }
.evidence-panel.positive { border-color: #ccebd7; background: #f5fbf7; }
.evidence-panel.caution { border-color: #f2dfb8; background: #fffaf1; }
.evidence-panel > strong, .business-panel > strong { color: #334155; font-size: 13px; }
.evidence-panel p { display: grid; grid-template-columns: 22px 1fr; gap: 7px; margin: 10px 0 0; color: #475569; font-size: 12px; line-height: 1.6; }
.evidence-panel p span { width: 20px; height: 20px; border-radius: 7px; display: inline-flex; align-items: center; justify-content: center; background: rgba(255, 255, 255, 0.9); color: #64748b; }
.business-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 9px; margin-top: 11px; }
.business-grid > div { min-width: 0; padding: 10px; border-radius: 9px; background: #f8fafc; }
.business-grid span { display: block; color: #64748b; font-size: 11px; }
.business-grid strong { display: block; margin-top: 5px; color: #334155; font-size: 12px; line-height: 1.5; word-break: break-word; }
.verification-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.dialog-footer { display: flex; justify-content: flex-end; gap: 8px; }

@media (max-width: 1100px) {
  .summary-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .filter-row { grid-template-columns: minmax(220px, 1fr) 180px 88px 74px; }
}

@media (max-width: 780px) {
  .summary-grid { grid-template-columns: 1fr; }
  .filter-row { grid-template-columns: 1fr; }
  .archive-header { flex-direction: column; }
  .archive-header .el-button { width: 100%; }
  .desktop-list { display: none; }
  .mobile-list { display: grid; gap: 11px; }
  .conclusion-card { padding: 14px; border: 1px solid #e2e8f0; border-radius: 13px; background: #fff; }
  .mobile-card-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
  .mobile-card-head strong { display: block; color: #0f172a; }
  .mobile-card-head span { display: block; margin-top: 3px; color: #64748b; font-size: 12px; }
  .mobile-summary { margin: 12px 0; color: #334155; font-size: 13px; line-height: 1.65; }
  .mobile-meta { display: flex; flex-wrap: wrap; justify-content: space-between; gap: 8px; color: #64748b; font-size: 11px; }
  .mobile-id { margin-top: 8px; color: #94a3b8; font-family: Consolas, Monaco, monospace; font-size: 11px; }
  .mobile-actions { display: grid; grid-template-columns: 1fr 1.4fr; gap: 9px; margin-top: 13px; }
  .mobile-actions .el-button { width: 100%; margin: 0; }
  .empty-state { padding: 38px 0; color: #94a3b8; text-align: center; }
  .detail-meta-grid, .evidence-grid, .detail-two-column, .business-grid, .verification-grid { grid-template-columns: 1fr; }
  .detail-heading { flex-direction: column; }
}
</style>
